import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

export interface Role {
  id: string;
  name: string;
  displayName: string;
  permissions: string[];
}

export interface AppModule {
  id: string;
  name: string;
  displayName: string;
  route: string;
  icon: string;
}

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  private currentRoleSubject = new BehaviorSubject<Role | null>(null);
  public currentRole$ = this.currentRoleSubject.asObservable();

  private roles: Role[] = [
    {
      id: 'admin',
      name: 'admin',
      displayName: 'Admin',
      permissions: ['*']
    },
    {
      id: 'labour',
      name: 'labour',
      displayName: 'Labour',
      permissions: ['case-management']
    },
    {
      id: 'owner',
      name: 'owner', 
      displayName: 'Owner',
      permissions: ['case-management', 'form-builder', 'dashboard', 'analytics', 'user-management']
    },
    {
      id: 'constructor',
      name: 'constructor',
      displayName: 'Constructor', 
      permissions: ['form-builder', 'form-data', 'case-management']
    }
  ];

  private allModules: AppModule[] = [
    { id: 'dashboard', name: 'dashboard', displayName: 'Dashboard', route: '/dashboard', icon: 'home' },
    { id: 'case-management', name: 'case-management', displayName: 'Case Management', route: '/case-management', icon: 'file-text' },
    { id: 'form-builder', name: 'form-builder', displayName: 'Form Builder', route: '/formio-builder', icon: 'form' },
    { id: 'form-data', name: 'form-data', displayName: 'Form Data', route: '/formio-data', icon: 'database' },
    { id: 'analytics', name: 'analytics', displayName: 'Analytics', route: '/analytics', icon: 'bar-chart' },
    { id: 'user-management', name: 'user-management', displayName: 'User Management', route: '/users', icon: 'team' }
  ];

  getRoles(): Observable<Role[]> {
    return of(this.roles);
  }

  setCurrentRole(role: Role): void {
    localStorage.setItem('currentRole', JSON.stringify(role));
    this.currentRoleSubject.next(role);
  }

  getCurrentRole(): Role | null {
    const stored = localStorage.getItem('currentRole');
    if (stored) {
      const role = JSON.parse(stored);
      if (this.currentRoleSubject.value?.id !== role.id) {
        this.currentRoleSubject.next(role);
      }
      return role;
    }
    // Set admin as default role
    const adminRole = this.roles.find(r => r.name === 'admin');
    if (adminRole && !this.currentRoleSubject.value) {
      localStorage.setItem('currentRole', JSON.stringify(adminRole));
      this.currentRoleSubject.next(adminRole);
      return adminRole;
    }
    return this.currentRoleSubject.value;
  }

  getAvailableModules(): Observable<AppModule[]> {
    const currentRole = this.currentRoleSubject.value;
    if (!currentRole) return of([]);
    
    if (currentRole.permissions.includes('*')) {
      return of(this.allModules);
    }
    
    const availableModules = this.allModules.filter(module => 
      currentRole.permissions.includes(module.id)
    );
    return of(availableModules);
  }

  hasPermission(permission: string): boolean {
    const currentRole = this.currentRoleSubject.value;
    if (!currentRole) return false;
    return currentRole.permissions.includes('*') || currentRole.permissions.includes(permission);
  }
}